import PaymentForm from '@alinta-digital/payment-form';
import { PaymentFormProps } from '@alinta-digital/payment-form/PaymentForm';
import { PaymentType } from '@alinta-digital/payment-form/types';
import {
  BamboraPaymentDetails,
  PaymentConfirmationProps,
  PaymentPrefilledDetailsProps
} from '@alinta-digital/payment-form/view';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { FC, useState } from 'react';
import { PaymentInput } from '../../api/PaymentController.types';
import { PaymentSource, PaymentTokenType, PaymentTransactionType, WebsitePaymentType } from '../../api/PaymentEnums';
import { GET_PAYPAL_RESPONSE, MAKE_SINGLE_PAYMENT_MUTATION, PayPalResultDTO } from '../../api/Payments.gql';
import { VALIDATE_ACCOUNT } from '../../api/Account.gql';
import PaymentLoading from '../PaymentLoading';
import { analyticsHelperWebPayment } from '@alinta-digital/payment-form/utils/analytics';

const Payment: FC<PaymentFormProps> = (props) => {
  const { paymentDetails, paymentSummary, paymentConfirmation }: PaymentFormProps = props;

  const [onceOffPaymentSuccess, setOnceOffPaymentSuccess] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState('');
  const [accountNumber, setAccountNumber] = useState(0);
  const [paymentRegion, setPaymentRegion] = useState('');
  const [isAccountNumberValid, setIsAccountNumberValid] = useState(false);
  const [message, setMessage] = useState('');
  const [paypalPaymentSuccess, setPaypalPaymentSucces] = useState(false);
  const [paymentType, setPaymentType] = useState(WebsitePaymentType.CARD);
  const [hasPayPalResponse, setHasPaypalResponse] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const isPaypalResultPage = urlParams?.has('SessionID') && urlParams.has('SST');
  const paymentPrefilledDetails: PaymentPrefilledDetailsProps = {
    accountNumber: urlParams.get('accountNumber') !== null ? parseInt(urlParams.get('accountNumber')!) : undefined,
    paymentAmount: urlParams.get('amount') || undefined
  };

  useQuery<PayPalResultDTO>(GET_PAYPAL_RESPONSE, {
    skip: !isPaypalResultPage || hasPayPalResponse,
    variables: {
      sessionID: urlParams.get('SessionID') ?? '',
      secureSessionToken: urlParams.get('SST') ?? ''
    },
    fetchPolicy: 'cache-first',
    onCompleted: (result) => {
      if (!result.getPaypalResponseWebsite) {
        setPaymentType(WebsitePaymentType.PAYPAL);
        setHasPaypalResponse(true);
        return;
      }

      setPaymentReceipt(result.getPaypalResponseWebsite.receipt ? result.getPaypalResponseWebsite.receipt : '');
      setPaypalPaymentSucces(result.getPaypalResponseWebsite.paypalTrnID ? true : false);
      setPaymentType(WebsitePaymentType.PAYPAL);
      setHasPaypalResponse(true);
    },
    onError: () => {
      setPaymentType(WebsitePaymentType.PAYPAL);
      setHasPaypalResponse(true);
    }
  });

  const [getCustomerAccount, { loading, called }] = useLazyQuery(VALIDATE_ACCOUNT, {
    onCompleted: (response) => {
      const { state } = response.customerAccountWebsite?.utilityAddress;
      setPaymentRegion(state);
      setIsAccountNumberValid(
        response.customerAccountWebsite && state.length >= 2 && !response.customerAccountWebsite.dormant
      );

      setMessage(
        !response.customerAccountWebsite.dormant
          ? state.length >= 2
            ? 'valid account'
            : 'Fail to get your account data, please refresh the page and try again'
          : 'Invalid account number'
      );
    },
    onError: (response) => {
      setIsAccountNumberValid(false);
      if (response.message.indexOf('400') > 0) {
        setMessage('Invalid account number');
      } else if (response.message.indexOf('500') > 0) {
        setMessage('Server error. Please try again later');
      } else {
        setMessage('Network error. Please check you network and try again');
      }
    }
  });

  const [makeOnceOffPaymentMutation] = useMutation(MAKE_SINGLE_PAYMENT_MUTATION, {
    onCompleted: (response) => {
      setOnceOffPaymentSuccess(response.makePaymentWebsite.success);
      setPaymentReceipt(response.makePaymentWebsite.ccPaymentReceipt?.receipt);
      setPaymentType(WebsitePaymentType.CARD);
    },
    onError: (error) => {
      console.error(error);
      setOnceOffPaymentSuccess(false);
    },
    fetchPolicy: 'no-cache'
  });

  // const [getOktaTokenMutation] = useMutation(GET_OKTA_TOKEN, {
  //   onCompleted: (response) => {
  //     setToken(response.getOktaToken.data);

  //     if (window && window.location) {
  //       if (isPaypalResultPage && !hasPayPalResponse) {
  //         payWithPayPalResult({
  //           variables: {
  //             sessionID: urlParams?.get('SessionID'),
  //             secureSessionToken: urlParams?.get('SST')
  //           }
  //         });
  //       }
  //     }

  //     // Set the Token timeout so that we refresh it if needed
  //     // setTimeout(
  //     //   () => getOktaTokenMutation({ variables: { input: config.digitalOkta } }),
  //     //   // Allow for time to get a new token...
  //     //   (response.getOktaToken.data.expiresIn + 1) * 1000
  //     // );
  //   },
  //   onError: (error) => {
  //     console.error(error);
  //   },
  //   fetchPolicy: 'no-cache'
  // });

  // useEffect(() => {
  //   getOktaTokenMutation({ variables: { input: config.digitalOkta } });
  // }, [getOktaTokenMutation]);

  paymentDetails.onValidateAccountNumber = async (updatedAccountNumber: number) => {
    if (accountNumber !== updatedAccountNumber) {
      setAccountNumber(updatedAccountNumber);
      setMessage('');

      await getCustomerAccount({
        variables: {
          accountNumber: updatedAccountNumber.toString()
        }
      });
      if (!loading && called) {
        return { isAccountNumberValid, message };
      }
    } else {
      return { isAccountNumberValid, message };
    }
  };

  paymentSummary.onSubmit = async (bamboraPaymentDetails: BamboraPaymentDetails, proceedToNextStep: () => void) => {
    const paymentDetails: PaymentInput = {
      reference: bamboraPaymentDetails.accountNumber,
      accountNumber: bamboraPaymentDetails.accountNumber,
      paymentTokenType: PaymentTokenType.STT,
      paymentToken: bamboraPaymentDetails.paymentToken,
      transactionType: PaymentTransactionType.PAYMENT,
      amount: bamboraPaymentDetails.amount,
      paymentRegion,
      paymentType: PaymentType.CUSTOM_CHECKOUT,
      source: PaymentSource.WEBSITE,
      emailAddress: bamboraPaymentDetails.emailAddress
    };
    await makeOnceOffPaymentMutation({
      variables: {
        update: paymentDetails
      }
    });
    proceedToNextStep();
  };

  const updatedPaymentConfirmation: PaymentConfirmationProps = paymentConfirmation;
  paymentConfirmation.success =
    paymentType && paymentType === WebsitePaymentType.PAYPAL ? paypalPaymentSuccess : onceOffPaymentSuccess;
  paymentConfirmation.receiptNumber = paymentReceipt;
  paymentConfirmation.paymentType = paymentType;
  const showPaymentsForm =
    (!isPaypalResultPage && paymentType === WebsitePaymentType.CARD) || (isPaypalResultPage && hasPayPalResponse);

  return (
    <>
      {showPaymentsForm && (
        <PaymentForm
          ga={analyticsHelperWebPayment}
          paymentDetails={paymentDetails}
          paymentPrefilledDetails={paymentPrefilledDetails}
          paymentSummary={paymentSummary}
          paymentConfirmation={updatedPaymentConfirmation}
          isUnAuthenticated={true}
        />
      )}
      {!showPaymentsForm && <PaymentLoading />}
    </>
  );
};

export default Payment;
